import { useEffect, useId, useRef, useState, type CSSProperties, type KeyboardEvent, type RefObject } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, PanelLeftClose, PanelLeftOpen, Settings } from 'lucide-react';
import BrandLogo from './BrandLogo';
import Tooltip from './ui/Tooltip';
import { useActiveNavPath, useNavGroups, type NavItem } from './navigation';
import type { NavGroupId } from './navModel';
import { usePermissions } from '../hooks/usePermissions';
import DocumentsToSignBadge from './documentsReview/DocumentsToSignBadge';
import ChatCountBadge from './ChatCountBadge';
import { usePendingBugReportsCount } from '../hooks/usePendingBugReportsCount';

/**
 * Sidebar (T-31, PRD §4.2 + §5, D2, Q-027 default).
 *
 * - 248 px, or the 72 px rail (icons + tooltips on hover and focus, the
 *   official symbol on top). Width snaps; the choice is remembered.
 * - "היום שלי", then static group headings (D2: they do not collapse).
 * - "ניהול והגדרות" is pinned to the bottom and is the only collapsible group
 *   (remembered in `ote_nav_groups`; opens by itself on one of its pages).
 *   C-35 (clarity, D-21): for a role that opens `/settings/system` it is ONE
 *   row, "הגדרות" (`aria-current` on any admin screen); the admin areas are
 *   listed on the settings page itself. Other roles keep the group.
 * - "Brand Air" (DESIGN_SYSTEM.md §2.8): the sidebar shares the canvas (no
 *   border); each icon takes its group's tint via `data-nav-group`.
 * - Active row: white pill + the brand dot (the logo's dot) in the
 *   gutter, `aria-current="page"`, most specific match only.
 * - No user row: the one user menu is the top-bar avatar (D4).
 * - First load, before `GET /permissions/me` answers (no cached answer):
 *   "היום שלי" and quiet placeholder rows, never a screen that may be closed
 *   and never a bare one-item menu (fix round 2026-10-07).
 * - C-19 (clarity): one scroller (the nav), a soft fade at a cut edge, 32 px
 *   rows under 900 px of height; group names are not headings (B-21).
 * - C-29 (clarity, D-18): the menu is ONE Tab stop (roving tabindex on the
 *   active item, else the first); ↑ / ↓ move between every visible item
 *   (the pinned list included while open), Home / End jump; Tab leaves.
 */

const GROUPS_KEY = 'ote_nav_groups';
/** The roving stop of the pinned group's toggle / the "הגדרות" row. */
const PINNED_STOP = 'pinned';

function readAdminOpen(): boolean {
  try { return JSON.parse(localStorage.getItem(GROUPS_KEY) || '{}').admin === true; } catch { return false; }
}
function writeAdminOpen(open: boolean) {
  try { localStorage.setItem(GROUPS_KEY, JSON.stringify({ admin: open })); } catch { /* storage unavailable */ }
}

const ICON = { size: 18, strokeWidth: 1.75 } as const;
/** Under 900 px of height the rows are 32 px (C-19). */
const ROW = 'h-9 px-3 [@media(max-height:900px)]:h-8';
const HEADING = 'px-3 pb-1 pt-5 text-xs font-medium text-text-3 [@media(max-height:900px)]:pt-3';
/** The fade is vertical here (`.scroll-fade` defaults to the reading direction). */
const FADE_STYLE = { '--fade-dir': 'to bottom' } as CSSProperties;

type RowKeys = { tabIndex: number; onKeyDown: (e: KeyboardEvent<HTMLElement>) => void };

/** FIX-D323-F2: the manager's "waiting for triage" count on bug reports (0 / non-manager: nothing shown). */
function PendingBugsBadge({ rail }: { rail: boolean }) {
  const count = usePendingBugReportsCount();
  if (count <= 0) return null;
  return rail
    ? <span className="absolute end-1 top-1 size-2 rounded-full bg-danger-500" aria-hidden="true" />
    : <span className="ms-auto min-w-5 rounded-full bg-danger-500 px-1.5 text-center text-xs font-semibold text-white tabular-nums" data-testid="bug-pending-count" aria-label={String(count)}>{count}</span>;
}

function Row({ item, group, active, rail, keys }: { item: NavItem; group: NavGroupId; active: boolean; rail: boolean; keys: RowKeys }) {
  const Icon = item.icon;
  const link = (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      data-nav-group={group}
      data-nav-stop={item.to}
      tabIndex={keys.tabIndex}
      onKeyDown={keys.onKeyDown}
      className={`nav-row ${rail ? 'size-11 justify-center relative' : ROW}`}
    >
      {active && <span className="nav-dot -start-2" aria-hidden="true" />}
      <Icon {...ICON} className="nav-row-icon flex-shrink-0" aria-hidden="true" />
      <span className={rail ? 'sr-only' : 'truncate'}>{item.label}</span>
      {item.id === 'documentsToSign' && <DocumentsToSignBadge rail={rail} />}
      {item.to === '/discussions' && <ChatCountBadge className={rail ? 'absolute -top-1 -end-1' : 'ms-auto'} />}
      {item.to === '/bug-reports' && <PendingBugsBadge rail={rail} />}
    </Link>
  );
  return <li>{rail ? <Tooltip label={item.label} className="flex justify-center">{link}</Tooltip> : link}</li>;
}

/** Which edges of a vertical scroller are cut off (for the fade); no work when nothing scrolls. */
function useScrollEdges(ref: RefObject<HTMLElement>, deps: unknown[]) {
  const [edges, setEdges] = useState({ start: false, end: false });
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => {
      const start = el.scrollTop > 4;
      const end = el.scrollHeight - el.clientHeight - el.scrollTop > 4;
      setEdges((prev) => (prev.start === start && prev.end === end ? prev : { start, end }));
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    ro?.observe(el);
    return () => { el.removeEventListener('scroll', update); ro?.disconnect(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return edges;
}

export default function Sidebar({ collapsed, onToggle }: { collapsed: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  const location = useLocation();
  const groups = useNavGroups();
  const { ready } = usePermissions();
  const active = useActiveNavPath(groups, location.pathname);
  const daily = groups.filter((g) => !g.pinned);
  const pinned = groups.find((g) => g.pinned);
  const pinnedHasActive = !!pinned?.items.some((i) => i.to === active);
  // C-35 (D-21): the role that opens the settings page gets one "הגדרות" row instead of the group.
  const settingsRow = !!pinned?.items.some((i) => i.id === 'systemSettings');
  const [adminOpen, setAdminOpen] = useState(readAdminOpen);
  const pinnedListId = useId();
  const navRef = useRef<HTMLElement>(null);

  // On one of its pages, the pinned group shows itself (not remembered).
  useEffect(() => { if (pinnedHasActive) setAdminOpen(true); }, [pinnedHasActive]);

  // C-29 (D-18): roving tabindex. The stop is the active item (the "הגדרות" row
  // on an admin screen), else the first item; after an arrow move, the item
  // that was reached. Never a stop that is not rendered (a collapsed group).
  const [roving, setRoving] = useState<string | null>(null);
  useEffect(() => { setRoving(null); }, [active]);
  const rendered = new Set<string>(daily.flatMap((g) => g.items.map((i) => i.to)));
  if (pinned) {
    rendered.add(PINNED_STOP);
    if (!settingsRow && adminOpen) for (const i of pinned.items) rendered.add(i.to);
  }
  const defaultStop = pinned && pinnedHasActive && (settingsRow || !adminOpen) ? PINNED_STOP : active;
  const firstStop = daily[0]?.items[0]?.to ?? PINNED_STOP;
  const stopKey = [roving, defaultStop, firstStop].find((k): k is string => !!k && rendered.has(k)) ?? firstStop;
  const keysFor = (key: string): RowKeys => ({ tabIndex: key === stopKey ? 0 : -1, onKeyDown: onNavKey });
  function onNavKey(e: KeyboardEvent<HTMLElement>) {
    if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
    const stops = Array.from(navRef.current?.querySelectorAll<HTMLElement>('[data-nav-stop]') ?? [])
      .filter((el) => el.getClientRects().length > 0);
    const i = stops.indexOf(e.target as HTMLElement);
    if (i < 0 || stops.length === 0) return;
    e.preventDefault();
    const j = e.key === 'Home' ? 0
      : e.key === 'End' ? stops.length - 1
        : e.key === 'ArrowDown' ? (i + 1) % stops.length
          : (i - 1 + stops.length) % stops.length;
    stops[j].focus();
    setRoving(stops[j].dataset.navStop ?? null);
  }

  // C-23 (B-03): keep the active row in view only when it really is out of
  // view (long lists, the pinned group); never a scroll on a plain load, and
  // never a focus change.
  useEffect(() => {
    const raf = requestAnimationFrame(() => {
      const el = navRef.current?.querySelector<HTMLElement>('[aria-current="page"]');
      const box = navRef.current;
      if (!el || !box) return;
      const r = el.getBoundingClientRect();
      const b = box.getBoundingClientRect();
      if (r.top < b.top || r.bottom > b.bottom) el.scrollIntoView({ block: 'nearest' });
    });
    return () => cancelAnimationFrame(raf);
  }, [active, adminOpen, collapsed]);

  const edges = useScrollEdges(navRef, [groups, adminOpen, collapsed, ready]);
  const toggleAdmin = () => setAdminOpen((o) => { writeAdminOpen(!o); return !o; });
  const rail = collapsed;

  return (
    <aside
      data-overlay-inert=""
      className={`app-sidebar fixed inset-y-0 start-0 z-sidebar hidden flex-col bg-sidebar md:flex ${rail ? 'w-rail' : 'w-sidebar'}`}
    >
      <div className={`flex flex-shrink-0 ${rail ? 'flex-col items-center gap-1 pb-2 pt-3' : 'h-14 items-center justify-between ps-4 pe-2'}`}>
        <Link to="/" className="flex items-center rounded-md" aria-label={t('shell.sidebar.home')}>
          {/* Expanded: the official mark; collapsed rail: the symbol (T-07, PRD §3.12). */}
          <BrandLogo variant={rail ? 'symbol' : 'mark'} decorative />
        </Link>
        <button
          type="button"
          onClick={onToggle}
          aria-label={rail ? t('shell.sidebar.expand') : t('shell.sidebar.collapse')}
          aria-expanded={!rail}
          className="btn-icon text-text-3 hover:bg-surface/60 hover:text-text transition-colors duration-micro"
        >
          {rail ? <PanelLeftOpen {...ICON} className="rtl-mirror" aria-hidden="true" /> : <PanelLeftClose {...ICON} className="rtl-mirror" aria-hidden="true" />}
        </button>
      </div>

      {/* C-19: the nav is the one scroller (the pinned block sits at its end), with a fade at a cut edge. */}
      <nav
        ref={navRef}
        aria-label={t('aria.mainNav')}
        className="scroll-fade flex min-h-0 flex-1 flex-col overflow-y-auto"
        style={FADE_STYLE}
        data-fade-start={edges.start ? '' : undefined}
        data-fade-end={edges.end ? '' : undefined}
      >
        <div className="px-3 pb-3">
          {daily.map((group, gi) => {
            const headingId = `nav-group-${group.id}`;
            return (
              <div key={group.id} role="group" aria-labelledby={group.label ? headingId : undefined}>
                {/* B-21: the group name labels the group; it is not a heading (the page's h1 comes first). */}
                {group.label && (
                  rail
                    ? <>
                        <p id={headingId} role="presentation" className="sr-only">{group.label}</p>
                        {gi > 0 && <div className="mx-2 my-2 h-px bg-border" aria-hidden="true" />}
                      </>
                    : <p id={headingId} role="presentation" className={HEADING}>{group.label}</p>
                )}
                <ul className={`flex flex-col ${rail ? 'items-stretch gap-1' : 'gap-0.5'}`}>
                  {group.items.map((item) => <Row key={item.to} item={item} group={group.id} active={item.to === active} rail={rail} keys={keysFor(item.to)} />)}
                </ul>
              </div>
            );
          })}
          {!ready && <NavSkeleton rail={rail} label={t('shell.sidebar.loading')} />}
        </div>

        {pinned && settingsRow && (
          <div className="mt-auto flex-shrink-0 border-t border-border px-3 py-2">
            {/* C-35 (D-21): one row, "הגדרות", current on any of the admin screens. */}
            <ul className={`flex flex-col ${rail ? 'items-stretch gap-1' : 'gap-0.5'}`}>
              <li>
                {(() => {
                  const link = (
                    <Link
                      to="/settings/system"
                      aria-current={pinnedHasActive ? 'page' : undefined}
                      data-nav-group="admin"
                      data-nav-stop={PINNED_STOP}
                      data-nav-settings=""
                      tabIndex={keysFor(PINNED_STOP).tabIndex}
                      onKeyDown={onNavKey}
                      className={`nav-row ${rail ? 'size-11 justify-center' : ROW}`}
                    >
                      {pinnedHasActive && <span className="nav-dot -start-2" aria-hidden="true" />}
                      <Settings {...ICON} className="nav-row-icon flex-shrink-0" aria-hidden="true" />
                      <span className={rail ? 'sr-only' : 'truncate'}>{t('shell.navGroup.settings')}</span>
                    </Link>
                  );
                  return rail ? <Tooltip label={t('shell.navGroup.settings')} className="flex justify-center">{link}</Tooltip> : link;
                })()}
              </li>
            </ul>
          </div>
        )}

        {pinned && !settingsRow && (
          <div className="mt-auto flex-shrink-0 border-t border-border px-3 py-2">
            {rail ? (
              <Tooltip label={pinned.label} className="flex justify-center">
                <button
                  type="button"
                  onClick={toggleAdmin}
                  aria-expanded={adminOpen}
                  aria-controls={pinnedListId}
                  data-nav-group="admin"
                  data-nav-stop={PINNED_STOP}
                  tabIndex={keysFor(PINNED_STOP).tabIndex}
                  onKeyDown={onNavKey}
                  className={`nav-row size-11 justify-center ${pinnedHasActive && !adminOpen ? 'text-accent-text' : ''}`}
                >
                  <Settings {...ICON} className="nav-row-icon" aria-hidden="true" />
                  <span className="sr-only">{pinned.label}</span>
                </button>
              </Tooltip>
            ) : (
              <button
                type="button"
                onClick={toggleAdmin}
                aria-expanded={adminOpen}
                aria-controls={pinnedListId}
                data-nav-group="admin"
                data-nav-stop={PINNED_STOP}
                tabIndex={keysFor(PINNED_STOP).tabIndex}
                onKeyDown={onNavKey}
                className={`nav-row w-full ${ROW}`}
              >
                <Settings {...ICON} className="nav-row-icon flex-shrink-0" aria-hidden="true" />
                <span className="flex-1 truncate text-start">{pinned.label}</span>
                <ChevronDown
                  size={16}
                  strokeWidth={1.75}
                  className={`flex-shrink-0 text-text-3 transition-transform duration-standard ${adminOpen ? 'rotate-180' : ''}`}
                  aria-hidden="true"
                />
              </button>
            )}
            {/* C-19: no inner scroll and no height cap; the nav scrolls as one. */}
            <ul
              id={pinnedListId}
              className={`mt-1 flex-col ${adminOpen ? 'flex' : 'hidden'} ${rail ? 'gap-1' : 'gap-0.5'}`}
            >
              {pinned.items.map((item) => <Row key={item.to} item={item} group={pinned.id} active={item.to === active} rail={rail} keys={keysFor(item.to)} />)}
            </ul>
          </div>
        )}
      </nav>
    </aside>
  );
}

/** Placeholder rows while the screens this person may open are not known yet. */
function NavSkeleton({ rail, label }: { rail: boolean; label: string }) {
  return (
    <div role="status" aria-busy="true" className="pt-5" data-nav-skeleton="">
      <span className="sr-only">{label}</span>
      <ul className={`flex flex-col ${rail ? 'items-center gap-1' : 'gap-0.5'}`} aria-hidden="true">
        {['w-24', 'w-32', 'w-20', 'w-28', 'w-24', 'w-16'].map((w, i) => (
          <li key={i} className={`flex items-center ${rail ? 'size-11 justify-center' : 'h-9 gap-3 px-3'}`}>
            <span className="size-4 flex-shrink-0 rounded-xs bg-surface-subtle animate-pulse motion-reduce:animate-none" />
            {!rail && <span className={`h-3 ${w} rounded-xs bg-surface-subtle animate-pulse motion-reduce:animate-none`} />}
          </li>
        ))}
      </ul>
    </div>
  );
}
